import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { groupBy } from 'lodash';
import { IconAdd } from '../../../../assets/icons/icon-add';
import { Carousel, CarouselItem, Header, NavHeader, Page, Tabs, TabType } from '../../../../components';
import { Tip } from '../../../../components/tip';
import { LearningScenarios } from '../../../../data-access/gql-types/graphql';
import { Device } from '../../components/device';
import { useDevicesAndChannelsContext } from '../../context/devices-and-channels';
import * as storage from '../../storage';
import { DeviceType } from '../../types';
import CheckContentWrapper, { PageType } from '../common/check-content-wrapper';
import { useAddDevice } from '../device-add/hooks/use-add-device';
import { UpdateDevices } from './update-devices';

type Element = {
  label: string;
  devices: DeviceType[];
};

const DeviceList: React.FC = () => {
  const { t: tc } = useTranslation('common');
  const { t } = useTranslation('device-list');
  const { deviceList } = useDevicesAndChannelsContext();
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const lastActiveTab = useRef(storage.getItem('lastActiveTabInDeviceList'));
  const { addDevice } = useAddDevice();

  useEffect(() => {
    setActiveTabIndex(Number(lastActiveTab.current) || 0);
  }, [setActiveTabIndex, lastActiveTab]);
  useEffect(() => storage.setItem('lastActiveTabInDeviceList', String(activeTabIndex)), [activeTabIndex]);

  const deviceListCount = useMemo(() => deviceList.filter(Boolean).length, [deviceList]);

  const groupedDevices = groupBy(deviceList, (device) => device && device.payload.model);

  const tabList: TabType[] = [{ label: tc('all') }, ...Object.keys(groupedDevices).map((model) => ({ label: model }))];

  const groupedDeviceList = tabList.map(({ label }) => {
    if (label === tc('all')) {
      return {
        label,
        elements: Object.entries(groupedDevices).map(([model, devices]) => ({
          label: model,
          devices,
        })),
      };
    } else {
      return {
        label,
        elements: [
          {
            label,
            devices: groupedDevices[label],
          },
        ],
      };
    }
  });

  return (
    <Page
      containerClassName="page__container--flex-height"
      isStickyHeader={deviceListCount !== 0}
      header={
        <>
          <NavHeader>
            <IconAdd onClick={addDevice} />
          </NavHeader>
          <Header title={t('devices')}>
            <UpdateDevices devicesToUpdate={deviceList} />
          </Header>
          {deviceListCount !== 0 && (
            <Tabs
              tabList={tabList}
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={setActiveTabIndex}
              tabsType="pills"
              moreButton="normal"
              moreListFullHeight
            />
          )}
        </>
      }
    >
      <CheckContentWrapper page={PageType.Devices}>
        {
          <Carousel setActiveIndex={setActiveTabIndex} activeIndex={activeTabIndex}>
            {groupedDeviceList.map((item: { label: string; elements: Element[] }, typeIndex: number) => (
              <CarouselItem key={typeIndex}>
                {item.elements.map((element: Element, i: number) => (
                  <div key={i}>
                    <Header title={element.label} subheader />
                    <div className="grid-list-24">
                      {element.devices.map((device) => (
                        <Device key={device.id} device={device} />
                      ))}
                    </div>
                    {i < item.elements.length - 1 ? <hr className="m-t-16 m-b-24" /> : null}
                  </div>
                ))}
              </CarouselItem>
            ))}
          </Carousel>
        }
      </CheckContentWrapper>
      <Tip tip={t('DeviceAddTip')} learningKey={LearningScenarios.AddDevice} point={<IconAdd />} top={30} right={16} />
    </Page>
  );
};

export default DeviceList;
